/** 프로필 카드(뽑기) 시스템 공용 타입 */

export type ProfileCardSetSource = 'admin' | 'workspace';

/** 프로필 카드 등급(희귀도) */
export const PROFILE_CARD_RARITIES = ['normal', 'rare', 'epic', 'legendary'] as const;
export type ProfileCardRarity = (typeof PROFILE_CARD_RARITIES)[number];

export function parseProfileCardRarity(value: unknown): ProfileCardRarity | null {
  if (value === 'normal' || value === 'rare' || value === 'epic' || value === 'legendary') {
    return value;
  }
  return null;
}

export type AdminProfileCardSet = {
  id: string;
  name: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
};

export type AdminProfileCardSlot = {
  id: string;
  setId: string;
  slotIndex: number;
  /** 개별 카드 이름(세트 이름과 별도). 비어 있으면 표시 시 N번 카드 */
  name: string;
  storagePath: string | null;
  imageVersion: number;
  /** 카드 등급. 이미지 미등록·미설정 시 null */
  rarity: ProfileCardRarity | null;
  updatedAt: string;
};

export type AdminProfileCardSetWithSlots = AdminProfileCardSet & {
  slots: AdminProfileCardSlot[];
};

/** 슬롯 중 이미지가 있고 등급이 정해져 뽑힐 수 있는 것 */
export function isDrawableSlot(slot: AdminProfileCardSlot): boolean {
  return Boolean(slot.storagePath) && slot.rarity != null;
}

/** 개별 카드 표시 이름 — 이름 없으면 N번 카드 */
export function profileCardSlotDisplayName(
  slot: Pick<AdminProfileCardSlot, 'name' | 'slotIndex'> | null | undefined,
  fallbackSlotIndex?: number
): string {
  const trimmed = (slot?.name ?? '').trim();
  if (trimmed) return trimmed;
  const n = slot?.slotIndex ?? fallbackSlotIndex ?? 0;
  return n > 0 ? `${n}번 카드` : '카드';
}

export type WorkspaceProfileCardSetSetting = {
  id: string;
  workspaceId: string;
  setId: string;
  enabled: boolean;
  priceCoins: number;
  updatedAt: string;
};

/** 워크스페이스(교사)가 등록하는 사용자 프로필 카드 세트 */
export type WorkspaceProfileCardSet = {
  id: string;
  workspaceId: string;
  name: string;
  sortOrder: number;
  enabled: boolean;
  priceCoins: number;
  createdAt: string;
  updatedAt: string;
};

export type WorkspaceProfileCardSetWithSlots = WorkspaceProfileCardSet & {
  slots: AdminProfileCardSlot[];
};

/** UI/스토어용 통합 세트 뷰 */
export type ProfileCardSetView = {
  id: string;
  name: string;
  sortOrder: number;
  source: ProfileCardSetSource;
  enabled: boolean;
  priceCoins: number;
  slots: AdminProfileCardSlot[];
  createdAt: string;
  updatedAt: string;
};

export const MAX_WORKSPACE_PROFILE_CARD_SETS = 2;

/** 프로필 카드 세트당 등록 가능한 카드(슬롯) 최대 개수 */
export const MAX_PROFILE_CARD_SLOTS_PER_SET = 30;

export type ClassMemberProfileCard = {
  id: string;
  workspaceId: string;
  gradeId: string;
  memberId: string;
  setId: string;
  slotIndex: number;
  sourceKind: ProfileCardSetSource;
  unlockedAt: string;
};

export function profileCardSetKey(source: ProfileCardSetSource, setId: string): string {
  return `${source}:${setId}`;
}

export function parseProfileCardSetKey(
  key: string
): { source: ProfileCardSetSource; setId: string } | null {
  const idx = key.indexOf(':');
  if (idx <= 0) return null;
  const source = key.slice(0, idx);
  const setId = key.slice(idx + 1);
  if ((source !== 'admin' && source !== 'workspace') || !setId) return null;
  return { source, setId };
}
